import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { createProduct } from "../features/products/productsSlice";
import { normalizeNumberInput } from "../shared/format";
import CategoryMultiSelect from "./CategoryMultiSelect";
import useGenerateBarcode from "../shared/useGenerateBarcode";

const EMPTY_FORM = {
  name: "",
  barcode: "",
  cost_price: "",
  wholesale_price: "",
  self_cost_price: "",
};

/**
 * Модалка быстрого создания товара — для случая, когда отсканированный штрихкод
 * не найден в базе и уходить на полную страницу создания дорого.
 *
 * Минимум полей: название, штрихкод, три цены, категории. Остальное
 * (описание, картинки, флаги) дозаполняется потом на /warehouse/products/:id/edit.
 *
 * onCreated(product) вызывается после успешного POST — вызывающая сторона решает,
 * что делать с созданным товаром.
 */
export default function QuickProductCreateModal({ open, initialBarcode, onCancel, onCreated }) {
  const dispatch = useDispatch();

  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedCategories, setSelectedCategories] = useState([]); // [{id,name}]
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { generate, generating } = useGenerateBarcode();

  // Каждое открытие — чистая форма с предзаполненным отсканированным штрихкодом.
  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY_FORM, barcode: String(initialBarcode || "").trim() });
    setSelectedCategories([]);
    setFormError(null);
    setSaving(false);
  }, [open, initialBarcode]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  const handleGenerateBarcode = async () => {
    const generated = await generate();
    if (generated) {
      setForm((f) => ({ ...f, barcode: generated }));
      setFormError(null);
    } else {
      setFormError("Не удалось сгенерировать штрихкод");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const name = String(form.name || "").trim();
    if (!name) {
      setFormError("Название обязательно");
      return;
    }

    const primaryBarcode = String(form.barcode || "").trim();
    const body = {
      name,
      barcode: primaryBarcode,
      description: "",
      cost_price: normalizeNumberInput(form.cost_price),
      wholesale_price: normalizeNumberInput(form.wholesale_price),
      self_cost_price: normalizeNumberInput(form.self_cost_price),
      is_active: true,
      category_ids: (selectedCategories || []).map((c) => c.id),
    };
    if (body.wholesale_price === null) delete body.wholesale_price;
    if (primaryBarcode) body.barcodes = [primaryBarcode];

    setSaving(true);
    setFormError(null);
    try {
      const created = await dispatch(createProduct({ body })).unwrap();
      onCreated(created);
    } catch (err) {
      const msg =
        (typeof err === "string" && err) ||
        err?.detail ||
        err?.barcode?.[0] ||
        err?.name?.[0] ||
        err?.non_field_errors?.[0] ||
        "Не удалось создать товар";
      setFormError(String(msg));
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onMouseDown={onCancel}>
      <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>Новый товар</div>
          <button type="button" onClick={onCancel} style={styles.modalClose} title="Закрыть">
            ×
          </button>
        </div>

        <form style={styles.modalBody} onSubmit={handleSubmit}>
          {formError && <div style={styles.error}>{formError}</div>}

          <div style={styles.field}>
            <label style={styles.label}>Название *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Название товара"
              style={styles.input}
              autoFocus
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Штрихкод</label>
            <div style={styles.barcodeRow}>
              <input
                type="text"
                value={form.barcode}
                onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))}
                placeholder="Основной штрихкод"
                style={{ ...styles.input, flex: 1, minWidth: 0 }}
              />
              <button
                type="button"
                onClick={handleGenerateBarcode}
                style={styles.cancelBtn}
                disabled={generating || saving}
                title="Сгенерировать внутренний штрихкод"
              >
                {generating ? "…" : "Сгенерировать"}
              </button>
            </div>
          </div>

          <div style={styles.priceRow}>
            <div style={styles.field}>
              <label style={styles.label}>Себестоимость</label>
              <input
                type="text"
                value={String(form.self_cost_price ?? "")}
                onChange={(e) => setForm((f) => ({ ...f, self_cost_price: e.target.value }))}
                placeholder="95.00"
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Розничная цена</label>
              <input
                type="text"
                value={String(form.cost_price ?? "")}
                onChange={(e) => setForm((f) => ({ ...f, cost_price: e.target.value }))}
                placeholder="100.00"
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Оптовая цена</label>
              <input
                type="text"
                value={String(form.wholesale_price ?? "")}
                onChange={(e) => setForm((f) => ({ ...f, wholesale_price: e.target.value }))}
                placeholder="120.00"
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Категории</label>
            <CategoryMultiSelect value={selectedCategories} onChange={setSelectedCategories} />
          </div>

          <div style={styles.modalActions}>
            <button type="button" onClick={onCancel} style={styles.cancelBtn} disabled={saving}>
              Отмена
            </button>
            <button type="submit" style={styles.confirmBtn} disabled={saving}>
              {saving ? "Создаю…" : "Создать и добавить"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 1000,
  },
  modal: {
    width: "min(560px, 92vw)",
    maxHeight: "90vh",
    background: "var(--c-card)",
    border: "1px solid var(--c-border-light)",
    borderRadius: 16,
    boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "14px 20px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  modalTitle: { fontSize: 17, fontWeight: 700, color: "var(--c-text)" },
  modalClose: {
    width: 30,
    height: 30,
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontSize: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBody: { padding: 20, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 14 },
  field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  priceRow: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 },
  barcodeRow: { display: "flex", gap: 8, alignItems: "center", minWidth: 0 },
  label: { fontSize: 14, fontWeight: 600, color: "var(--c-text-muted)" },
  input: {
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    fontFamily: "inherit",
  },
  error: {
    padding: "10px 12px",
    borderRadius: 8,
    background: "var(--c-danger-dim)",
    color: "var(--c-danger)",
    border: "1px solid var(--c-danger)",
    fontSize: 15,
    fontWeight: 700,
  },
  modalActions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 },
  confirmBtn: {
    padding: "9px 20px",
    borderRadius: 99,
    border: "none",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
  },
  cancelBtn: {
    padding: "9px 20px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 15,
  },
};
